/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IconDescription,
  IconRating,
  IconSpec,
} from '@/components/Icon/IconProductDetail'
import ImageOptimize from '@/components/ImageOptimize'
import { labelPositionMapStyle } from '@/constants/positions'
import { useNotification } from '@/containers'
import { useLightbox } from '@/hooks/useLightBox'
import { type ProductDetail } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import type { Settings } from 'react-slick'
import Slider from 'react-slick'
import ProductPrice from '../EntrySummary/ProductPrice'

interface Props {
  images: string[]
  alt: string
  product: ProductDetail
  label: ProductDetail
  isOutOfStock: boolean
  refs: any
  video?: string
  handleClickRef: (ref: any) => void
}
const breakpoints = [2160, 1080, 640, 384, 256, 128]
export const Gallery: FC<Props> = ({
  images,
  alt,
  product,
  label,
  isOutOfStock,
  refs,

  handleClickRef,
}: Props) => {
  const [activeSlide, setActiveSlide] = useState(1)
  const totalNum = images?.length
  const { openLightBox, renderLightBox } = useLightbox()
  const sliderMain = useRef<Slider | null>(null)
  const settingsMain: Settings = {
    slidesToScroll: 1,
    arrows: false,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    className: 'overflow-hidden productGalleryMain',
    afterChange: (current: number) => setActiveSlide(current + 1),
  }
  const { systemConfig } = useNotification()
  const percent = Math.ceil(100 - (product?.finalPrice / product?.price) * 100)
  return (
    <div className={`image-summary relative`}>
      {renderLightBox({
        slides: images.map((e, i) => {
          const width = 400 * 2
          const height = 400 * 2
          return {
            src:
              i === 0 && product.videoUrl && product.videoThumbnail
                ? product.videoThumbnail
                : e,
            type: i === 0 && product.videoUrl ? undefined : 'image',
            width,
            videoUrl: product?.videoUrl,
            height,
            srcSet: breakpoints.map(breakpoint => {
              const breakpointHeight = Math.round((height / width) * breakpoint)
              return {
                src:
                  i === 0 && product.videoUrl && product.videoThumbnail
                    ? product.videoThumbnail
                    : e,
                width: breakpoint,
                height: breakpointHeight,
              }
            }),
          }
        }),
      })}
      {percent > 0 && (
        <span className='discount flex items-center gap-[2px] absolute left-3 top-[60px] z-20'>
          <span className='flex items-center text-percent bg-primary-second leading-none font-bold rounded-lg h-9 px-2 text-white text-xl'>
            <span className='value'>-{percent}</span>
            <span className='unit'>%</span>
          </span>
        </span>
      )}
      {images?.length === 0 ? (
        <ImageOptimize
          quality={100}
          className='cartImage w-full object-cover'
          src={
            systemConfig?.imagePlaceholder ||
            process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
            ''
          }
          loading='lazy'
          alt={alt}
          width={400}
          height={400}
        />
      ) : (
        <div className={`image-summary-image relative py-[50px] px-8`}>
          <Slider {...settingsMain} ref={sliderMain}>
            {images.map((e, i) => {
              return (
                <div className='relative flex justify-center ' key={i}>
                  <ImageOptimize
                    onClick={() => openLightBox(i)}
                    className='relative mx-auto aspect-square w-full cursor-pointer object-contain'
                    height={512}
                    width={512}
                    alt={alt}
                    src={
                      i === 0 && product.videoUrl && product.videoThumbnail
                        ? product.videoThumbnail
                        : e
                    }
                    priority={true}
                  />
                  {i === 0 && product.videoUrl && (
                    <svg
                      className='absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer'
                      width={70}
                      onClick={() => openLightBox(i)}
                      height={70}
                      viewBox='0 0 70 70'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M35 0C15.67 0 0 15.67 0 35C0 54.33 15.67 70 35 70C54.33 70 70 54.33 70 35C69.9795 15.6787 54.3215 0.0206543 35 0Z'
                        fill='#F1005D'
                      />
                      <polygon points='28,22 28,48 48,35' fill='white' />
                    </svg>
                  )}
                </div>
              )
            })}
          </Slider>
          {label &&
            label?.labels.map((item, index) => {
              return (
                <>
                  {item?.image ? (
                    <span
                      className={`${labelPositionMapStyle.get(
                        item?.position
                      )} label absolute hiddenScrollBar w-max`}
                      style={{ zIndex: 20 - index }}
                      key={index}
                    >
                      <ImageOptimize
                        src={item?.image}
                        alt={getTranslate(item?.name)}
                        width={217}
                        height={40}
                        className='w-full align-middle h-auto'
                      />
                    </span>
                  ) : (
                    ''
                  )}
                </>
              )
            })}
          {images ? (
            <div className='absolute bottom-3 right-3 z-10'>
              <span className='bg-dark-800/20 rounded-[32px] text-xs overflow-hidden text-white font-bold flex items-center h-[28px] px-4'>
                <span className=''>{activeSlide}</span>
                <span className=''>/{totalNum}</span>
              </span>
            </div>
          ) : (
            ''
          )}
          <div
            className={`linkSections items-center justify-center gap-2 absolute left-[50%] translate-x-[-50%] bottom-2.5 z-[50] flex w-full max-w-[440px] flex-wrap`}
          >
            <div
              className={`item text-xs`}
              onClick={() => handleClickRef(refs.descRef)}
            >
              <div
                className={`relative w-[70px] h-[70px] text-center cursor-pointer px-1 flex justify-center flex-wrap py-2 rounded-lg shadow-base bg-white`}
                data-name={'#description'}
              >
                <IconDescription />
                <span className='mt-1 block h-8 leading-4'>Mô tả sản phẩm</span>
              </div>
            </div>
            <div
              className={`item text-xs`}
              onClick={() => handleClickRef(refs.specRef)}
            >
              <div
                className={`relative w-[70px] h-[70px] text-center cursor-pointer px-1 flex justify-center flex-wrap py-2 rounded-lg shadow-base bg-white`}
                data-name={'#price'}
              >
                <IconSpec />

                <span className='mt-1 block h-8 leading-4'>
                  Thông số kỹ thuật
                </span>
              </div>
            </div>
            <div
              className={`item text-xs`}
              onClick={() => handleClickRef(refs.reviewRef)}
            >
              <div
                className={`relative w-[70px] h-[70px] text-center cursor-pointer px-1 flex justify-center flex-wrap py-2 rounded-lg shadow-base bg-white`}
                data-name={'#review'}
              >
                <IconRating />
                <span className='mt-1 block h-8 leading-4'>Đánh giá</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {product?.flashSale && (
        <ProductPrice
          isOutOfStock={isOutOfStock}
          priceAtWarehouse={product?.priceAtWarehouse}
          originPrice={product?.price}
          finalPrice={product?.finalPrice}
          preOrderPrice={product?.preOrder?.salePrice}
          flashSale={product?.flashSale}
          productId={product?._id}
          hasGift={product?.gifts && product?.gifts?.length > 0}
          preOrder={product?.preOrder}
        />
      )}
    </div>
  )
}
